export function getMembershipNextStepCopy(input: {
  status: string;
  amountLabel: string;
  nextBillingLabel: string | null;
  contractStartLabel?: string | null;
  contractEndLabel: string | null;
  autoRenew: boolean;
  collectionMethod?: 'AUTO_CHARGE' | 'MANUAL_INVOICE' | 'PREPAID';
  failedPaymentCount: number;
  cancellationEffectiveLabel: string | null;
  expiredLabel: string | null;
  startsInFuture?: boolean;
  /**
   * Given on a free, office-only option: the base is free and it ends with its
   * term. `billsExtras` when extra covered units still bill each period.
   */
  complimentary?: { billsExtras: boolean; extrasPaidThroughNextBilling?: boolean };
}): string {
  const free = input.complimentary;
  switch (input.status.toUpperCase()) {
    case 'PENDING':
      if (input.startsInFuture && input.contractStartLabel) {
        return free && !free.billsExtras
          ? `Scheduled to activate on ${input.contractStartLabel}. Coverage begins that day.`
          : `Scheduled to activate on ${input.contractStartLabel}. Coverage and billing begin that day.`;
      }
      if (free) {
        if (!free.billsExtras) return 'Activate to start coverage. The base is free.';
        return input.collectionMethod === 'PREPAID'
          ? 'Activate to start coverage. The base is free; extra units bill up front for the term.'
          : 'Activate to start coverage. The base is free; extra units bill each period.';
      }
      if (input.collectionMethod === 'MANUAL_INVOICE') {
        return 'Activate to start coverage. The first recurring invoice will be created after the first billing period.';
      }
      return 'Activate the membership to start billing.';
    case 'ACTIVE':
      if (free) {
        const ends = input.contractEndLabel
          ? ` Ends ${input.contractEndLabel} unless you extend its end date.`
          : '';
        if (free.billsExtras) {
          // A prepaid term billed its extra units once, up front.
          if (input.collectionMethod === 'PREPAID') {
            return `The base is free; extra units were billed up front for the term.${ends}`;
          }
          if (free.extrasPaidThroughNextBilling && input.nextBillingLabel) {
            return `Extra units are paid through ${input.nextBillingLabel}.${ends}`;
          }
          const bill = input.collectionMethod === 'AUTO_CHARGE' ? 'charge' : 'invoice';
          return input.nextBillingLabel
            ? `Next ${bill} ${input.nextBillingLabel} for ${input.amountLabel}, extra units only.${ends}`
            : `The base is free; extra units bill each period.${ends}`;
        }
        return input.contractEndLabel
          ? `Free until ${input.contractEndLabel}. It ends then unless you extend its end date.`
          : 'Free · nothing is billed.';
      }
      if (input.collectionMethod === 'MANUAL_INVOICE') {
        return input.nextBillingLabel
          ? `Next invoice will be created ${input.nextBillingLabel} for ${input.amountLabel}.`
          : 'Active · recurring invoices are generated automatically for manual collection.';
      }
      if (!input.autoRenew && input.contractEndLabel) {
        return `Current term ends ${input.contractEndLabel}. Renewal requires review.`;
      }
      if (!input.autoRenew && input.nextBillingLabel) {
        return `Next payment is due ${input.nextBillingLabel} for ${input.amountLabel}.`;
      }
      return input.nextBillingLabel
        ? `Renews ${input.nextBillingLabel} for ${input.amountLabel}. Auto-renew is on.`
        : 'Active and billing on schedule.';
    case 'PAST_DUE':
      // Billed by invoice, it is past due because an invoice is unpaid — past
      // its grace, or reopened when the bank returned the payment — and there
      // is no payment method on file to update.
      if (input.collectionMethod === 'MANUAL_INVOICE' || input.collectionMethod === 'PREPAID') {
        return 'Payment past due. Collect the open invoice to keep it active.';
      }
      return input.failedPaymentCount > 0
        ? `Payment past due · ${input.failedPaymentCount} failed attempt${
            input.failedPaymentCount === 1 ? '' : 's'
          }. Update the payment method to keep it active.`
        : 'Payment past due. Update the payment method to keep it active.';
    case 'SUSPENDED':
      return 'Membership is suspended. Reactivate to resume billing.';
    case 'CANCELLED':
      return input.cancellationEffectiveLabel
        ? `Cancelled · coverage ends ${input.cancellationEffectiveLabel}. Reactivate to resume billing.`
        : 'Cancelled. Reactivate to resume billing.';
    case 'EXPIRED':
      return input.expiredLabel
        ? `Membership expired on ${input.expiredLabel}.`
        : 'Membership expired.';
    default:
      return '';
  }
}

/**
 * Sold on an estimate or on a job's own lines: its fee rides that document's
 * invoice, and paying it is what turns the membership on.
 */
export function isDocumentSale(source: string): boolean {
  const upper = source.toUpperCase();
  return upper === 'ESTIMATE' || upper === 'JOB';
}

export function canManuallyActivateMembership(input: {
  status: string;
  source: string;
  collectionMethod?: 'AUTO_CHARGE' | 'MANUAL_INVOICE' | 'PREPAID';
  contractStartDate?: string | null;
  todayDateKey?: string;
  activationPayment?: {
    settled: boolean;
    startsInFuture: boolean;
  };
}): boolean {
  if (input.status.toUpperCase() !== 'PENDING') return false;
  if (input.activationPayment?.startsInFuture === true) return false;
  if (input.activationPayment && input.activationPayment.settled === false) {
    return false;
  }
  if (
    !isDocumentSale(input.source) &&
    input.collectionMethod === 'PREPAID' &&
    input.activationPayment?.settled !== true
  ) {
    return false;
  }
  if (
    input.todayDateKey &&
    input.contractStartDate?.slice(0, 10) &&
    input.contractStartDate.slice(0, 10) > input.todayDateKey
  ) {
    return false;
  }
  if (!isDocumentSale(input.source)) return true;

  return (
    input.activationPayment?.settled === true && input.activationPayment.startsInFuture === false
  );
}

export function getMembershipCollectionLabel(
  collectionMethod?: 'AUTO_CHARGE' | 'MANUAL_INVOICE' | 'PREPAID',
): string {
  switch (collectionMethod) {
    case 'AUTO_CHARGE':
      return 'Charge saved card';
    case 'PREPAID':
      return 'Prepay full term';
    default:
      return 'Invoice customer';
  }
}

export function getEstimateMembershipActivationCopy(input: {
  status: string;
  source: string;
  collectionMethod?: 'AUTO_CHARGE' | 'MANUAL_INVOICE' | 'PREPAID';
  contractStartLabel: string | null;
  activationPayment?: {
    required: boolean;
    settled: boolean;
    startsInFuture: boolean;
    sourceJobId: string | null;
    invoice: {
      invoiceNumber: string;
      status: string;
      balanceDue: number;
    } | null;
  };
  formatBalance: (amount: number) => string;
}): string | null {
  const payment = input.activationPayment;
  if (
    input.status.toUpperCase() !== 'PENDING' ||
    (!isDocumentSale(input.source) && input.collectionMethod !== 'PREPAID') ||
    payment?.required !== true
  ) {
    return null;
  }

  if (payment.settled) {
    return payment.startsInFuture && input.contractStartLabel
      ? `Payment is complete. This membership will activate on ${input.contractStartLabel}.`
      : 'Payment is complete. Membership activation is being processed.';
  }

  const invoice = payment.invoice;
  if (!invoice) {
    if (!isDocumentSale(input.source) && input.collectionMethod === 'PREPAID') {
      return 'Create the full-term invoice and collect payment before activation.';
    }
    return payment.sourceJobId
      ? 'Create the membership invoice from the linked job before activation.'
      : 'Create the membership invoice from the approved estimate before activation.';
  }

  const status = invoice.status.toUpperCase();
  if (status === 'PAID') {
    return `Invoice ${invoice.invoiceNumber} is paid. Membership activation is being processed.`;
  }
  if (status === 'PARTIALLY_PAID') {
    return `Invoice ${invoice.invoiceNumber} is partially paid. ${input.formatBalance(
      invoice.balanceDue,
    )} remains before activation.`;
  }
  if (status === 'DRAFT') {
    return `Invoice ${invoice.invoiceNumber} must be sent and paid before activation.`;
  }
  return `Waiting for invoice ${invoice.invoiceNumber} to be paid. Balance: ${input.formatBalance(
    invoice.balanceDue,
  )}.`;
}
